/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useState } from 'react';
import { ContextStatus, JobType } from '@leedpulse-monorepo/enums';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import getJobs from '../../../domain/use_cases/jobs/getJobs';
import JobWithContact from '../../../domain/models/@aggregates/JobWithContact';
import getNextJobs from '../../../domain/use_cases/jobs/getNextJobs';
import { generateActionChartData } from '../../../core/utils/commonUtils';

export const JOBS_FETCH_LIMIT = 10; // NOTE : It can be 25

interface ActionChartData {
  categories: string[],
  series: { name: string; data: number[]; jobType?: JobType }[]
}

const JobsContext = createContext({
  count: 0,
  hitsPerPage: JOBS_FETCH_LIMIT,
  items: [] as JobWithContact[],
  lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
  scopedSubscriptionId: null as string | null,
  scopedContactId: null as string | null,
  actionChartData: {} as ActionChartData,
  fetch: (
    subscriptionIdToScopeOn: string | null,
    contactIdToScopeOn: string | null,
    limit?: number
  ) => {},
  fetchNext: () => {},
  fetchActionChart: (scenarioId?: string, userUid?: string, sender?: string | null) => {},
  reset: () => {},
  status: ContextStatus.IDLE,
  chartStatus: ContextStatus.IDLE
});

export const JobsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [count, setCount] = useState(0);
  const [hitsPerPage, setHitsPerPage] = useState(JOBS_FETCH_LIMIT);
  const [items, setItems] = useState([] as JobWithContact[]);
  const [actionChartData, setActionChartData] = useState({} as ActionChartData);
  const [lastVisible, setLastVisible] = useState(
    {} as QueryDocumentSnapshot<DocumentData, DocumentData>
  );
  const [scopedSubscriptionId, setScopedSubscriptionId] = useState(
    null as string | null
  );
  const [scopedContactId, setScopedContactId] = useState(null as string | null);
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [chartStatus, setChartStatus] = useState(ContextStatus.IDLE);

  const fetch = async (
    subscriptionIdToScopeOn: string | null,
    contactIdToScopeOn: string | null,
    limit?: number
  ) => {
    const effectiveLimit = limit ?? hitsPerPage;
    if (limit !== undefined) setHitsPerPage(limit);
    setScopedSubscriptionId(subscriptionIdToScopeOn);
    setScopedContactId(contactIdToScopeOn);
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getJobs(
        effectiveLimit,
        undefined,
        subscriptionIdToScopeOn,
        contactIdToScopeOn
      );
      setItems(useCaseResponse.jobs);
      setLastVisible(useCaseResponse.lastVisible);
      setCount(useCaseResponse.totalCollectionCount);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchNext = async () => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getJobs(
        hitsPerPage,
        lastVisible,
        scopedSubscriptionId,
        scopedContactId
      );
      setItems([...items, ...useCaseResponse.jobs]);
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchActionChart = async (scenarioId?: string, userUid?: string, sender?: string | null) => {
    setChartStatus(ContextStatus.LOADING);
    if (!scopedSubscriptionId) return;
    try {
      const useCaseResponse = await getNextJobs(30, scopedSubscriptionId, scenarioId, userUid);
      const chartData = generateActionChartData(useCaseResponse.jobs, 30, sender);
      setActionChartData(chartData);
      setChartStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setChartStatus(ContextStatus.ERROR);
    }
  };

  const reset = () => {
    setCount(0);
    setHitsPerPage(JOBS_FETCH_LIMIT);
    setItems([]);
    setLastVisible({} as QueryDocumentSnapshot<DocumentData, DocumentData>);
    setScopedSubscriptionId(null);
    setActionChartData({} as ActionChartData);
    setStatus(ContextStatus.IDLE);
    setChartStatus(ContextStatus.IDLE);
  };

  return (
    <JobsContext.Provider
      value={{
        count,
        hitsPerPage,
        items,
        lastVisible,
        scopedSubscriptionId,
        scopedContactId,
        actionChartData,
        fetch,
        fetchNext,
        fetchActionChart,
        reset,
        status,
        chartStatus
      }}
    >
      {children}
    </JobsContext.Provider>
  );
};

export default JobsContext;
